'use client';

import { useCallback, useState } from 'react';
import { TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CANVAS_ALT_MAX_M,
  CANVAS_ALT_MIN_M,
  effectiveWaypointAltitude,
  parseWaypointAltitudeInput,
  type DraftWaypoint,
} from '../missions/_components/missionCanvasShared';

// ---------------------------------------------------------------------------
// The enumerated route: one row per waypoint, with the altitude that waypoint
// will actually be flown at.
//
// Shared by compose and by revise-a-saved-route because the two lists are the
// same list. They were duplicated markup already; adding a per-point altitude
// to both copies would have made them two subtly different answers to "what
// will this point fly at", which is the one question this row exists to answer.
//
// THE ALTITUDE RULE, stated once here because the UI has to express it:
//   - The mission altitude is the DEFAULT. A point that has never been given one
//     of its own follows it, and keeps following it when the operator changes it.
//     Such a row shows the mission altitude as a PLACEHOLDER — greyed, present,
//     and not a value.
//   - A point the operator typed an altitude into is that operator's decision.
//     Nothing rewrites it, including a later change to the mission altitude. Such
//     a row shows a real value in full contrast.
//   - Clearing the box is the way back: it removes the override rather than
//     setting the altitude to zero or to whatever the mission says at that
//     instant.
// ---------------------------------------------------------------------------

export interface RouteWaypointListProps {
  waypoints: DraftWaypoint[];
  selectedIndex: number | null;
  /** Indices with a blocking violation — the same ones drawn red on the map. */
  invalidIndices: ReadonlySet<number>;
  /** The mission altitude. Every point without one of its own flies at this. */
  missionAltitudeM: number;
  onSelect: (index: number | null) => void;
  /**
   * Edit one point's altitude, or `null` to put it back on the mission altitude.
   * Omitted ⇒ the route is locked and altitudes render as read-only text.
   */
  onAltitudeChange?: (index: number, altitudeM: number | null) => void;
  /** Omitted ⇒ no per-point removal (a locked route, or one the panel will not shorten). */
  onRemove?: (index: number) => void;
  /** A write is in flight — the route must not move under it. */
  disabled?: boolean;
  /**
   * Height cap for the scroller. Required rather than defaulted: the panel that owns the layout
   * is the only thing that knows how much room the list may take without pushing the form it
   * sits in off screen.
   */
  className?: string;
}

export function RouteWaypointList({
  waypoints,
  selectedIndex,
  invalidIndices,
  missionAltitudeM,
  onSelect,
  onAltitudeChange,
  onRemove,
  disabled = false,
  className,
}: RouteWaypointListProps) {
  /**
   * What the operator has TYPED, per waypoint id, before it becomes an altitude.
   *
   * Needed because the parsed value is clamped and the raw text is not: driving the box from the
   * parsed number would rewrite "1" to "10" under the operator's cursor on the way to typing
   * "150". The box shows exactly what was typed; the row underneath says what was stored.
   *
   * Keyed by waypoint id, never by index — a removal from the middle of the route renumbers every
   * row after it, and an index-keyed buffer would move a half-typed altitude onto another point.
   */
  const [rawById, setRawById] = useState<Record<string, string>>({});

  const editable = onAltitudeChange !== undefined;

  const handleRawChange = useCallback(
    (wp: DraftWaypoint, index: number, raw: string) => {
      // Drop buffered text for points that no longer exist, so a long editing session cannot
      // accumulate entries for removed waypoints.
      const live = new Set(waypoints.map((w) => w.id));
      setRawById((prev) => {
        const next: Record<string, string> = {};
        for (const [id, value] of Object.entries(prev)) {
          if (live.has(id)) next[id] = value;
        }
        next[wp.id] = raw;
        return next;
      });
      onAltitudeChange?.(index, parseWaypointAltitudeInput(raw).meters);
    },
    [waypoints, onAltitudeChange],
  );

  if (waypoints.length === 0) return null;

  // `relative` on the scroller, for the same reason every scroll container in these panels
  // carries it: a scroller that is not a containing block does not contain absolutely
  // positioned descendants, which then escape its clip and project their un-scrolled height
  // onto a positioned ancestor far above. Nothing in here is abspos today; the rule is.
  return (
    <ul className={cn('relative overflow-y-auto rounded border border-subtle divide-y divider', className)}>
      {waypoints.map((wp, i) => {
        const overridden = wp.altitudeM !== undefined;
        const raw = rawById[wp.id];
        /**
         * The buffered text is honoured only while it still EXPLAINS the point's current
         * altitude. Anything that changes the route from outside this box — a revert, a reload of
         * the stored route, a fresh load — leaves the old text behind, and a row showing "250"
         * over a point that is back at 80 is the panel contradicting itself about what would be
         * flown. When it no longer explains the draft, the draft wins.
         */
        const parsedRaw = raw !== undefined ? parseWaypointAltitudeInput(raw) : null;
        const rawExplainsDraft = parsedRaw !== null && parsedRaw.meters === (wp.altitudeM ?? null);
        // The box shows what was typed while it is being typed; otherwise it shows the stored
        // override, and nothing at all when the point follows the mission (the placeholder does
        // the talking there).
        const value = rawExplainsDraft ? raw! : overridden ? String(wp.altitudeM) : '';
        const note = rawExplainsDraft ? parsedRaw!.note : null;
        const selected = i === selectedIndex;
        const invalid = invalidIndices.has(i);

        return (
          <li key={wp.id}>
            <div
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 text-[11px] font-mono tabular-nums',
                selected && 'bg-primary/10',
                invalid && 'text-status-red-fg',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(selected ? null : i)}
                aria-pressed={selected}
                className="flex-1 min-w-0 truncate text-left rounded-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                WP{i + 1} · {wp.lat.toFixed(5)}, {wp.lng.toFixed(5)}
              </button>

              {editable ? (
                <input
                  type="number"
                  inputMode="numeric"
                  min={CANVAS_ALT_MIN_M}
                  max={CANVAS_ALT_MAX_M}
                  value={value}
                  placeholder={String(missionAltitudeM)}
                  disabled={disabled}
                  onChange={(e) => handleRawChange(wp, i, e.target.value)}
                  // Spelled out rather than left to the placeholder: read aloud, a greyed number
                  // and a typed one are the same number, and which of the two this is decides
                  // whether changing the mission altitude will move this point.
                  aria-label={
                    overridden
                      ? `Waypoint ${i + 1} altitude, ${wp.altitudeM} metres. Clear to follow the mission altitude.`
                      : `Waypoint ${i + 1} altitude, following the mission altitude of ${missionAltitudeM} metres.`
                  }
                  className={cn(
                    'w-14 shrink-0 rounded border bg-background px-1 py-0.5 text-right text-[11px] font-mono tabular-nums',
                    'focus:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:opacity-50',
                    overridden
                      ? 'border-border text-foreground'
                      : 'border-subtle text-muted-foreground placeholder:text-muted-foreground',
                  )}
                />
              ) : (
                <span className="shrink-0 text-muted-foreground">
                  {effectiveWaypointAltitude(wp, missionAltitudeM)}
                </span>
              )}
              <span aria-hidden="true" className="shrink-0 text-[10px] text-muted-foreground">m</span>

              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  disabled={disabled}
                  aria-label={`Remove waypoint ${i + 1}`}
                  className="shrink-0 p-0.5 text-muted-foreground hover:text-status-red-fg transition-colors disabled:opacity-50"
                >
                  <X className="size-3" strokeWidth={1.5} aria-hidden="true" />
                </button>
              )}
            </div>
            {/* Clamping is reported, never applied silently — the same rule the mission altitude
                box follows, for the same reason.
                Given the weight of what it reports — the altitude this point will actually be
                flown at is not the one on screen — it carries the panel's standard warning
                treatment (icon + 11px amber) rather than the quietest type in the component,
                and sits on a tinted band so it reads as attached to ITS row and not as a
                footnote under the list. `role="alert"` because it appears in response to
                something the operator just typed. */}
            {note && (
              <p
                role="alert"
                className="flex items-start gap-1.5 mx-2 mb-1 rounded border border-status-amber/40 bg-status-amber/15 px-1.5 py-1 text-[11px] text-status-amber-fg"
              >
                <TriangleAlert className="size-3.5 shrink-0 mt-px" strokeWidth={1.5} aria-hidden="true" />
                {note}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
